/**
 * Этап 5 / KS-02 — «Адресаты проекта»: pick the main addressee and the CC list from
 * the client's existing contacts.
 *
 * ── The complaint this answers.
 * Ксения created a project and a hiring manager already in the system was not offered
 * for selection — the contact had to be typed in again. So this is a LIST, not a
 * type-ahead: PM's point is that EER users do not remember what a client's contact is
 * called, they remember the company. The client is already known from the project, and
 * every one of its contacts is shown, unpaged.
 *
 * ── Why a radio + checkboxes and not a multi-select.
 * The two roles are not symmetric: there is at most ONE main addressee (the DB enforces
 * it with a partial unique index) and any number of copies. A single control that could
 * express "two primaries" would be a control that can express an unsavable state.
 *
 * ── Relationship to the header's contact fields.
 * The header still edits the current contact's own name/email — that CORRECTS a
 * directory row. This card CHOOSES which rows are addressed. Both end up consistent
 * because the API mirrors `projects.contact_id` and the `primary` link inside one
 * transaction (`syncPrimaryLink`).
 */
import { useEffect, useState, type JSX } from 'react';
import type { ContactDTO } from '@eer/shared';
import { errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import {
  useClientContacts,
  useCreateContact,
  useDeleteContact,
  useProjectContacts,
  useSetProjectContacts,
} from './directoryApi.js';
import {
  contactLabel,
  lacksEmail,
  pickerClientId,
  sameSelection,
  selectionFrom,
  visibleContacts,
  withCcToggled,
  withAddedPrimary,
  withPrimary,
  type ContactSelection,
} from './projectContactsModel.js';

export interface ProjectContactsCardProps {
  readonly projectId: number;
  /** The project's own client id (null when none resolved yet). */
  readonly clientId: number | null;
}

const BTN_PRIMARY =
  'rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50';
const BTN_GHOST =
  'rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm disabled:opacity-50';
const INPUT =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]';

export function ProjectContactsCard({
  projectId,
  clientId,
}: ProjectContactsCardProps): JSX.Element {
  const toast = useToast();
  const savedQuery = useProjectContacts(projectId);
  const saved = savedQuery.data;

  const listClientId = pickerClientId(clientId, saved);
  const contactsQuery = useClientContacts(listClientId);
  const setContacts = useSetProjectContacts(projectId);
  const createContact = useCreateContact();
  const removeContact = useDeleteContact();
  const { confirm, confirmDialog } = useConfirm();

  const [selection, setSelection] = useState<ContactSelection>(() => selectionFrom(saved));
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  // Adopt the saved set once it arrives (and after a save re-reads it). Keyed on the
  // saved value, so the user's in-progress ticks are not clobbered by an unrelated
  // refetch — a refetch that returns the same set produces the same `selectionFrom`.
  useEffect(() => {
    if (saved) setSelection(selectionFrom(saved));
  }, [saved]);

  const contacts = visibleContacts(contactsQuery.data ?? [], saved);
  const dirty = !sameSelection(selection, selectionFrom(saved));
  const busy = setContacts.isPending || createContact.isPending || removeContact.isPending;

  async function save(next: ContactSelection, successMessage = 'Addressees saved'): Promise<void> {
    try {
      await setContacts.mutateAsync({
        primary_contact_id: next.primaryId,
        cc_contact_ids: [...next.ccIds],
      });
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the addressees'));
    }
  }

  /**
   * KS2-09 — take a contact off the project (Ксения: «нужна кнопка удаления контакта
   * из проекта — сейчас все контакты остаются в проекте»).
   *
   * Two steps, in this order, because they answer two halves of one complaint. Clearing
   * the ticks alone does not make the row go away: the list shows the CLIENT's contacts,
   * so a person added by mistake stays visible on every project of that client. Deleting
   * the directory row alone is impossible while this project still links it — the server
   * refuses (`InUseError`), and rightly so.
   *
   * A contact used by OTHER projects is not deleted: the server refuses and says by how
   * many. That is reported as it is rather than swallowed — the row IS off this project
   * by then, which is what the user asked for, and the rest is not this screen's call.
   */
  async function detachContact(contact: ContactDTO): Promise<void> {
    const ok = await confirm({
      title: 'Remove contact?',
      message: `Remove ${contact.name.trim() || 'this contact'} from the project? It will also be deleted from the client directory if no other project uses it.`,
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
      testId: 'project-contacts-remove-confirm',
    });
    if (!ok) return;

    const next: ContactSelection = {
      primaryId: selection.primaryId === contact.id ? null : selection.primaryId,
      ccIds: selection.ccIds.filter((id) => id !== contact.id),
    };
    setSelection(next);
    // Saved unconditionally: the link may exist on the server while the local
    // selection has already been edited, and a delete attempted before the unlink
    // commits is the 409 this flow exists to avoid.
    await save(next, '');

    try {
      await removeContact.mutateAsync(contact.id);
      toast.success('Contact removed');
    } catch (error) {
      toast.error(errorMessage(error, 'Removed from the project, but the contact was kept'));
    }
  }

  /**
   * Create a contact for THIS client and make it the main addressee.
   *
   * The previous main addressee is moved into CC, not dropped (E02-F08-T07): the user
   * added a missing person, they did not ask to remove the one already there.
   *
   * Selecting it immediately is the point of the button: the user opened it because
   * the person they need is missing, so creating it and leaving it unselected would
   * make them do the same job twice. The new set is saved in the same gesture — a
   * created-but-unlinked contact is invisible on the project.
   */
  async function addContact(): Promise<void> {
    const name = newName.trim();
    if (!name) {
      toast.error('Enter the contact name');
      return;
    }
    try {
      const created = await createContact.mutateAsync({
        client_id: listClientId,
        name,
        email: newEmail.trim() || null,
      });
      const next = withAddedPrimary(selection, created.id);
      setSelection(next);
      setAdding(false);
      setNewName('');
      setNewEmail('');
      await save(next);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not create the contact'));
    }
  }

  return (
    <section
      data-testid="project-contacts-card"
      className="rounded-lg border border-[var(--color-border)] bg-white p-4"
    >
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
            Project addressees
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Primary recipient and CC. The list holds the contacts of the selected client.
          </p>
        </div>
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={!dirty || busy}
          data-testid="project-contacts-save"
          onClick={() => void save(selection)}
        >
          {setContacts.isPending ? 'Saving…' : 'Save'}
        </button>
      </header>

      {savedQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : listClientId === null ? (
        // Not an error state: a project created from an email may have no client yet.
        // Saying so is more useful than an empty list the user cannot explain.
        <p data-testid="project-contacts-no-client" className="text-sm text-slate-500">
          Select a client first — the contacts come from its directory.
        </p>
      ) : (
        <>
          {contactsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading contacts…</p>
          ) : contacts.length === 0 ? (
            <p data-testid="project-contacts-empty" className="text-sm text-slate-500">
              This client has no contacts yet.
            </p>
          ) : (
            <ul
              className="divide-y divide-[var(--color-border)]"
              data-testid="project-contacts-list"
            >
              <li className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 pb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                <span>To</span>
                <span>CC</span>
                <span>Contact</span>
                <span className="sr-only">Remove</span>
              </li>
              {contacts.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  isPrimary={selection.primaryId === c.id}
                  isCc={selection.ccIds.includes(c.id)}
                  disabled={busy}
                  onPrimary={() => setSelection((s) => withPrimary(s, c.id))}
                  onCc={() => setSelection((s) => withCcToggled(s, c.id))}
                  onRemove={() => void detachContact(c)}
                />
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={BTN_GHOST}
              disabled={busy}
              data-testid="project-contacts-clear-primary"
              onClick={() => setSelection((s) => withPrimary(s, null))}
            >
              Clear primary
            </button>
            {adding ? null : (
              <button
                type="button"
                className={BTN_GHOST}
                disabled={busy}
                data-testid="project-contacts-add-toggle"
                onClick={() => setAdding(true)}
              >
                + New contact
              </button>
            )}
          </div>

          {adding ? (
            <div
              data-testid="project-contacts-add-form"
              className="mt-3 grid gap-2 rounded-md border border-[var(--color-border)] bg-slate-50 p-3 sm:grid-cols-2"
            >
              <label className="text-xs text-slate-600">
                Name
                <input
                  className={INPUT}
                  value={newName}
                  data-testid="project-contacts-new-name"
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <label className="text-xs text-slate-600">
                Email
                <input
                  className={INPUT}
                  value={newEmail}
                  data-testid="project-contacts-new-email"
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </label>
              <div className="flex gap-2 sm:col-span-2">
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={busy}
                  data-testid="project-contacts-new-save"
                  onClick={() => void addContact()}
                >
                  Add and select
                </button>
                <button
                  type="button"
                  className={BTN_GHOST}
                  disabled={busy}
                  onClick={() => {
                    setAdding(false);
                    setNewName('');
                    setNewEmail('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
      {confirmDialog}
    </section>
  );
}

interface ContactRowProps {
  readonly contact: ContactDTO;
  readonly isPrimary: boolean;
  readonly isCc: boolean;
  readonly disabled: boolean;
  readonly onPrimary: () => void;
  readonly onCc: () => void;
  readonly onRemove: () => void;
}

function ContactRow({
  contact,
  isPrimary,
  isCc,
  disabled,
  onPrimary,
  onCc,
  onRemove,
}: ContactRowProps): JSX.Element {
  const noEmail = lacksEmail(contact);
  return (
    <li className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 py-2 text-sm">
      <input
        type="radio"
        name="project-primary-contact"
        checked={isPrimary}
        disabled={disabled}
        aria-label={`Primary: ${contact.name}`}
        data-testid={`contact-primary-${contact.id}`}
        onChange={onPrimary}
      />
      <input
        type="checkbox"
        checked={isCc}
        // A contact with no address cannot be a recipient; the row stays visible so
        // the user can go and fill the address in instead of wondering where it went.
        // Already in CC (e.g. a no-email former primary moved there by "Add and select")
        // stays untickable — otherwise the user could see it ticked but not remove it.
        disabled={disabled || (noEmail && !isCc)}
        aria-label={`CC: ${contact.name}`}
        data-testid={`contact-cc-${contact.id}`}
        onChange={onCc}
      />
      <span className={noEmail ? 'text-slate-500' : undefined}>{contactLabel(contact)}</span>
      <button
        type="button"
        disabled={disabled}
        title="Remove from the project"
        aria-label={`Remove ${contact.name}`}
        data-testid={`contact-remove-${contact.id}`}
        onClick={onRemove}
        className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 hover:text-red-600 disabled:opacity-50"
      >
        <i className="fa-solid fa-trash" aria-hidden />
      </button>
    </li>
  );
}
